import React__default, { forwardRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { RuleItemInnerVisualSlot } from './_RuleItemInnerVisualSlot.js';
import { useRefObjectAsForwardedRef } from '../../hooks/useRefObjectAsForwardedRef.js';
import { useOutsideClick } from '../../hooks/useOutsideClick.js';
import Box from '../../components/Box/Box.js';
import Stack from '../../components/Stack/Stack.js';
import AtlasBadge from '../../components/AtlasBadge/AtlasBadge.js';
import { get } from '../../constants.js';
import sx from '../../sx.js';

/**
 *
 * A rule item is used to describe an item in a workflow and provides actions a user can take.
 *
 * There are several variants; each variant will compose the Standard rule builder.
 * (Standard) Rule Item. Selectable. No collapse. Can potentially be dismissed.
 * (Collapsible) Rule Item. Collapsible. Can potentially be selected. No dismiss. Not draggable.
 * (Draggable) Rule Item. Draggable. Can potentially be dismissed. Can potentially be collapsed.
 *
 * spacing: 'compact' (max-height: 40px) | 'default' (max-height: 48px) | 'relaxed' (max-height: 84px) | 'none' (0 padding; consumers can add with sx prop)
 *
 */
const RuleItem = /*#__PURE__*/forwardRef(({
  id,
  children,
  loading,
  counter,
  spacing = 'default',
  loaderPosition = 'auto',
  leadingVisual: LeadingVisual,
  trailingVisual: TrailingVisual,
  trailingAction,
  disabled: disabledProp,
  isSelected: isSelectedProp,
  sx: sxProp,
  as = 'div',
  onSelect,
  testId,
  ...props
}, forwardedRef) => {
  const wrapperTestId = `${testId}-wrapper`;
  const containerRef = React__default.useRef(null);
  useRefObjectAsForwardedRef(forwardedRef, containerRef);
  const showLeadingLoadingIndicator = loading && (loaderPosition === 'leading' || Boolean(LeadingVisual && loaderPosition !== 'trailing'));
  const showTrailingLoadingIndicator = loading && (loaderPosition === 'trailing' || Boolean(loaderPosition === 'auto' && !LeadingVisual));
  const [isActive, setIsActive] = useState(isSelectedProp);
  const [isDisabled, setIsDisabled] = useState(disabledProp);

  // const isRuleItemSelected = isSelectedProp || isActive
  const isRuleItemSelected = isSelectedProp;
  const isRuleItemDisabled = disabledProp || isDisabled;

  // function handleRuleItemSelect() {
  //   setIsActive(true)
  // }

  function handleRuleItemSelect(event) {
    if (isSelectedProp) return;
    if (onSelect) {
      // onSelect(event, event.clientY)
      onSelect(event);
    }
  }
  function handleRuleItemBlur() {
    setIsActive(false);
  }
  useOutsideClick({
    onClickOutside: handleRuleItemBlur,
    containerRef
  });
  return /*#__PURE__*/React__default.createElement(Wrapper, {
    as: as,
    id: id,
    spacing: spacing,
    ref: containerRef,
    isSelected: isRuleItemSelected,
    disabled: isRuleItemDisabled,
    onClick: handleRuleItemSelect,
    sx: sxProp,
    "data-testid": testId ? wrapperTestId : 'undefined',
    "data-selected": isRuleItemSelected
  }, /*#__PURE__*/React__default.createElement(Box, {
    sx: {
      display: 'flex',
      gap: 2,
      alignItems: 'center'
    }
  }, /*#__PURE__*/React__default.createElement(RuleItemInnerVisualSlot, {
    visualPosition: "leading",
    showLoadingIndicator: showLeadingLoadingIndicator,
    hasLoadingIndicator: typeof loading === 'boolean'
  }, typeof LeadingVisual === 'function' ? /*#__PURE__*/React__default.createElement(LeadingVisual, null) : LeadingVisual), /*#__PURE__*/React__default.createElement(Stack, {
    direction: `row`,
    size: 4,
    sx: {
      alignItems: 'center'
    }
  }, React__default.Children.map(React__default.Children.toArray(children), (child, idx) => {
    if (!child) {
      return null;
    }
    return /*#__PURE__*/React__default.createElement(React__default.Fragment, {
      key: idx
    }, spacing ? /*#__PURE__*/React__default.cloneElement(child, {
      spacing
    }) : child);
  }))), /*#__PURE__*/React__default.createElement(RuleItemInnerVisualSlot, {
    visualPosition: "trailing",
    showLoadingIndicator: showTrailingLoadingIndicator,
    hasLoadingIndicator: typeof loading === 'boolean'
  }, typeof TrailingVisual === 'function' ? /*#__PURE__*/React__default.createElement(TrailingVisual, null) : TrailingVisual, counter && /*#__PURE__*/React__default.createElement(CounterBadge, {
    appearance: isRuleItemSelected ? `primary` : isRuleItemDisabled ? `disabled` : `default`
  }, counter), trailingAction ? /*#__PURE__*/React__default.cloneElement(trailingAction, {
    isRuleItemDisabled
  }) : null));
});
const CounterBadge = styled(AtlasBadge).withConfig({
  displayName: "RuleItem__CounterBadge",
  componentId: "sc-1g8aix1-0"
})([""]);
const Wrapper = styled(Box).withConfig({
  displayName: "RuleItem__Wrapper",
  componentId: "sc-1g8aix1-1"
})(["border-radius:", ";display:flex;align-items:center;justify-content:space-between;gap:", ";width:100%;padding:", ";max-height:", ";background-color:", ";color:", ";border:1px solid;border-color:", ";transition:background-color 0.1s ease-out,box-shadow 0.15s cubic-bezier(0.47,0.03,0.49,1.38);transition:", ";border-color:", ";user-select:none;.rule-item-visual{svg,rect{fill:", ";}}&:hover{background-color:", ";border:", ";border-color:", ";cursor:", ";}", " &:focus{border-color:", ";}&:active{background-color:", ";border:1px solid ", ";}", ""], get('radii.2'), get('space.4'), p => p.spacing === 'none' ? '' : get('space.3'), p => p.spacing === 'compact' ? '40px' : p.spacing === 'relaxed' ? '84px' : p.spacing === 'default' ? '48px' : p.spacing === 'none' ? '' : '84px', p => p.disabled ? get('colors.background.disabled') : get('colors.background.neutral.default.[default]'), p => p.disabled ? get('colors.text.disabled') : get('colors.text.default'), get('colors.border.default'), p => p.isSelected ? 'revert' : '350ms', p => p.isSelected ? get('colors.border.selected') : 'transparent', p => p.disabled ? get('colors.icon.disabled') : '', get('colors.background.neutral.subtle.hovered'), p => p.isSelected ? '1px solid' : '1px solid', p => p.isSelected ? get('colors.border.selected') : p.disabled ? 'transparent' : get('colors.border.default'), p => p.disabled ? 'not-allowed' : 'pointer', p => p.isSelected && css(["&:hover{box-shadow:0 0 0 2px ", ";transition:background-color 0.1s ease-out,box-shadow 0.45s cubic-bezier(0.47,0.03,0.49,1.38);}"], get('colors.border.focused')), get('colors.border.focused'), get('colors.background.neutral.subtle.hovered'), get('colors.border.selected'), sx);
RuleItem.displayName = 'RuleItem';
var RuleItem$1 = RuleItem;

export { RuleItem$1 as default };
